import {useReducer} from 'react';

import {
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ActivityTypesEnum, BusinessHoursParametersEnum} from '../../../../../editors/commons/paramModel';
import {ClientRuleAction} from '../../../../ruleActionModel';
import {RuleConditionTypesEnum, UnrepliedAfterCondition} from '../../../../ruleConditionModel';
import {RuleIntegration} from '../../../../ruleIntegrationModel';
import {SlaEditorActionTypesEnum} from '../state/slaEditorActions';
import {slaEditorSpecificReducer} from '../state/slaEditorReducer';
import {SlaEditorSpecificState, SlaEditorSubsetsEnum} from '../state/slaEditorState';

/*
 * Types.
 */

export interface SlaEditorSpecificActions {
  onUpdateBreachUnreplied: HandlerOf<UnrepliedAfterCondition>;

  onAddWarningUnreplied: Handler;
  onUpdateWarningUnreplied: HandlerOf<UnrepliedAfterCondition>;
  onDeleteWarningUnreplied: HandlerOf<UnrepliedAfterCondition>;

  onAddBreachAction: HandlerOf<ClientRuleAction>;
  onUpdateBreachAction: (
    predicate: PredicateOf<ClientRuleAction>,
    update: (action: ClientRuleAction) => ClientRuleAction,
  ) => void;
  onDeleteBreachAction: (predicate: PredicateOf<ClientRuleAction>) => void;

  onAddWarningAction: HandlerOf<ClientRuleAction>;
  onUpdateWarningAction: (
    predicate: PredicateOf<ClientRuleAction>,
    update: (action: ClientRuleAction) => ClientRuleAction,
  ) => void;
  onDeleteWarningAction: (predicate: PredicateOf<ClientRuleAction>) => void;

  onSetBreachTag: HandlerOf<number | undefined>;
  onSetWarningTag: HandlerOf<number | undefined>;
}

/*
 * Hooks.
 */

export function useSlaEditorSpecifState(
  initialState: SlaEditorSpecificState,
  ruleIntegration: RuleIntegration,
): SlaEditorSpecificActions & {slaEditorState: SlaEditorSpecificState} {
  const [slaEditorState, dispatch] = useReducer(slaEditorSpecificReducer, initialState);

  const onUpdateBreachUnreplied = (condition: UnrepliedAfterCondition) => {
    dispatch({type: SlaEditorActionTypesEnum.SET_BREACH_UNREPLIED, payload: condition});
  };

  const onAddWarningUnreplied = () => {
    dispatch({
      type: SlaEditorActionTypesEnum.SET_WARNING_UNREPLIED,
      payload: {
        integration_id: 0,
        type: RuleConditionTypesEnum.UNREPLIED_AFTER,
        params: {
          business_hours: BusinessHoursParametersEnum.REGARDLESS_BH,
          activity_type: ActivityTypesEnum.INBOUND,
        },
      },
    });
  };

  const onUpdateWarningUnreplied = (condition: UnrepliedAfterCondition) => {
    dispatch({type: SlaEditorActionTypesEnum.SET_WARNING_UNREPLIED, payload: condition});
  };

  const onDeleteWarningUnreplied = () => {
    dispatch({type: SlaEditorActionTypesEnum.SET_WARNING_UNREPLIED, payload: undefined});
  };

  function makeOnAddAction(subset: SlaEditorSubsetsEnum) {
    return (action: ClientRuleAction) => {
      dispatch({type: SlaEditorActionTypesEnum.ADD_ACTION, payload: {action, subset}});
    };
  }

  const onAddBreachAction = makeOnAddAction(SlaEditorSubsetsEnum.BREACH);
  const onAddWarningAction = makeOnAddAction(SlaEditorSubsetsEnum.WARNING);

  function makeOnUpdateAction(subset: SlaEditorSubsetsEnum) {
    return (
      predicate: PredicateOf<ClientRuleAction>,
      update: (action: ClientRuleAction) => ClientRuleAction,
    ) => {
      dispatch({type: SlaEditorActionTypesEnum.UPDATE_ACTION, payload: {predicate, update, subset}});
    };
  }

  const onUpdateBreachAction = makeOnUpdateAction(SlaEditorSubsetsEnum.BREACH);
  const onUpdateWarningAction = makeOnUpdateAction(SlaEditorSubsetsEnum.WARNING);

  function makeOnDeleteAction(subset: SlaEditorSubsetsEnum) {
    return (predicate: PredicateOf<ClientRuleAction>) => {
      dispatch({type: SlaEditorActionTypesEnum.DELETE_ACTION, payload: {predicate, subset}});
    };
  }

  const onDeleteBreachAction = makeOnDeleteAction(SlaEditorSubsetsEnum.BREACH);
  const onDeleteWarningAction = makeOnDeleteAction(SlaEditorSubsetsEnum.WARNING);

  function makeOnSetTag(subset: SlaEditorSubsetsEnum) {
    return (tagId: number | undefined) =>
      dispatch({type: SlaEditorActionTypesEnum.SET_TAG, payload: {tagId, subset}});
  }

  const onSetBreachTag = makeOnSetTag(SlaEditorSubsetsEnum.BREACH);
  const onSetWarningTag = makeOnSetTag(SlaEditorSubsetsEnum.WARNING);

  return {
    slaEditorState,

    onUpdateBreachUnreplied,

    onAddWarningUnreplied,
    onUpdateWarningUnreplied,
    onDeleteWarningUnreplied,

    onAddBreachAction,
    onUpdateBreachAction,
    onDeleteBreachAction,

    onAddWarningAction,
    onUpdateWarningAction,
    onDeleteWarningAction,

    onSetBreachTag,
    onSetWarningTag,
  };
}
